/**
 * OpenGraph metadata for rich social previews.
 *
 * H5 games provide these values so social networks can render rich cards when
 * a share link is posted.
 */
export interface ShareMetadata {
  /** Preview title shown in link cards (e.g. Twitter, iMessage). */
  title?: string;
  /** Preview description shown under the title. */
  description?: string;
  /** Preview image URL (must be HTTPS for most social platforms). */
  imageUrl?: string;
}

/**
 * The piece a share link is really about, when it isn't your app itself.
 *
 * An app that hosts creator-made content can credit a share's clicks to that
 * piece's author instead of to the app. Name only the piece. The host scopes it
 * to your app id, so a target can never point somewhere you don't own.
 */
export interface ShareContentTarget {
  /** What kind of piece this is. */
  format: 'interactive-fiction' | 'video';
  /** Your UGC entry id for the piece. Must not contain ":". */
  entryId: string;
}

/**
 * A pending share intent: what the host attaches when the player uses the
 * HOST app's own share buttons (game toolbar, in-game info panel) while this
 * game session is running. Same shapes and server caps as `shareLinkAsync`.
 */
export interface ShareIntent {
  /** Arbitrary share parameters for your game. Delivered to the recipient via `app.resolveLaunchIntent()`. */
  shareParams: Record<string, string>;
  /** Optional OpenGraph metadata for the minted link's rich preview. */
  metadata?: ShareMetadata;
  /** Optional cosmetic slug, same semantics as `shareLinkAsync`. */
  slug?: string;
  /** Optional content target, same semantics as `shareLinkAsync`. */
  target?: ShareContentTarget;
}

export interface SocialQRCodeOptions {
  /** Size of the QR code image in pixels. */
  size?: number;
  /** Margin around the QR code in pixels. */
  margin?: number;
  /** Output format for the rendered QR code. */
  format?: 'png' | 'svg';
}

export interface ShareLinkResult {
  shareUrl: string;
  shareLinkId: string;
}

export interface QRCodeResult {
  shareUrl: string;
  qrCode: string;
  shareLinkId: string;
}

export interface ShareClickData {
  clickerProfileId: string;
  metadata: Record<string, string>;
  /** Epoch milliseconds */
  createdAt: number;
  /** Epoch milliseconds */
  updatedAt: number;
}

/** Allowed MIME types for file sharing via the SDK bridge. */
export const SHARE_FILE_ALLOWED_MIME_TYPES = [
  'video/mp4',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

export type ShareFileMimeType = (typeof SHARE_FILE_ALLOWED_MIME_TYPES)[number];

/** Max file size in bytes (10 MB). */
export const SHARE_FILE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export interface ShareFileOptions {
  /**
   * File data as Blob, ArrayBuffer, or a pre-encoded base64 string.
   * Blob and ArrayBuffer are encoded to base64 automatically before
   * crossing the bridge. If passing a string, it MUST be a valid
   * base64-encoded representation of the file bytes (NOT a data URI).
   */
  data: Blob | ArrayBuffer | string;
  filename: string;
  mimeType: ShareFileMimeType;
  /** Optional title passed alongside the file to the share sheet. */
  title?: string;
  /** Optional text passed alongside the file to the share sheet. */
  text?: string;
}

export interface ShareFileResult {
  /** True if the user dismissed the share sheet without sharing. */
  cancelled: boolean;
}

export interface CanShareFileResult {
  /** True if the platform supports navigator.share with files. */
  supported: boolean;
}

/** Platforms a creator can expose a "follow me" link for. */
export const FOLLOW_ME_PLATFORMS = ['x', 'instagram', 'tiktok', 'discord'] as const;

export type FollowMePlatform = (typeof FOLLOW_ME_PLATFORMS)[number];

export interface FollowMeResult {
  completed: boolean;
  /** Present when completed is false because the creator hasn't set that handle. */
  reason?: 'not_configured';
}

/** Platforms accepted as a `composeSocialPostAsync` targeting hint. */
export const SOCIAL_COMPOSE_PLATFORMS = [
  'x',
  'reddit',
  'tiktok',
  'instagram',
] as const;

export type SocialComposePlatform = (typeof SOCIAL_COMPOSE_PLATFORMS)[number];

/** Max length for a compose post body, after control characters are stripped. */
export const COMPOSE_POST_MAX_TEXT_LENGTH = 500;

/** Max length for a compose post title (used when routed to Reddit). */
export const COMPOSE_POST_MAX_TITLE_LENGTH = 300;

/** Valid subreddit names (bare name, no `r/` prefix). */
export const COMPOSE_POST_SUBREDDIT_PATTERN = /^[A-Za-z0-9_]{1,21}$/;

/** Optional image/video attached to a composed post. */
export interface ComposeSocialPostMedia {
  /** Same encoding rules as {@link ShareFileOptions.data}. */
  data: Blob | ArrayBuffer | string;
  filename: string;
  mimeType: ShareFileMimeType;
}

export interface ComposeSocialPostOptions {
  /** Post body / caption. Max 500 characters. */
  text: string;
  /**
   * When present, the host mints a tracked share link (same machinery as
   * `shareLinkAsync`) and attaches it to the post. There is deliberately no
   * `url` parameter — games can never inject an arbitrary destination URL.
   */
  shareParams?: Record<string, string>;
  /** Optional image/video, same encoding rules as `shareFileAsync`. */
  media?: ComposeSocialPostMedia;
  /**
   * Optional targeting hint. If the hinted platform can't work on the current
   * surface, the host degrades to its generic destination chooser instead of
   * throwing — games never branch on OS/browser/destination support.
   */
  platform?: SocialComposePlatform;
  /** Post title, used only when routed to Reddit. Max 300 characters. */
  title?: string;
  /** Target subreddit (bare name, no `r/` prefix), used only when routed to Reddit. */
  subreddit?: string;
  /**
   * OpenGraph metadata for the tracked share link minted from `shareParams`
   * (same semantics as `shareLinkAsync`). Fields left absent fall back to the
   * game's values (name/thumbnail). Ignored when `shareParams` is absent — no
   * link is minted, so there is nothing to attach a preview to.
   */
  metadata?: ShareMetadata;
}

export interface ComposeSocialPostResult {
  /**
   * True when the user acted on a destination and it actually opened. False
   * when they dismissed the sheet, cancelled the OS share sheet (web), or
   * when the destination could not be shown (e.g. a popup blocker — the
   * composer URL is left on the clipboard — or the game could not leave
   * fullscreen).
   *
   * Native caveat: iOS/Android do not report whether the user cancelled the
   * system share sheet (same limitation as {@link ShareFileResult.cancelled}),
   * so share-sheet destinations resolve `completed: true` once the sheet
   * opens.
   */
  completed: boolean;
  /** Destination that handled the post (e.g. 'x', 'reddit', 'tiktok', 'instagram', 'copy-link', 'more'). */
  destination?: string;
}

/**
 * Social distribution API used by H5 games.
 *
 * This unified API powers both share links and QR codes, backed by a single
 * Firestore document that stores arbitrary launch parameters supplied by the
 * game.
 */
export interface SocialApi {
  /**
   * Create a share link and invoke the host platform's native share UX.
   *
   * Platform behaviour:
   * - iOS / Android: opens the native share sheet.
   * - Web: copies the generated URL to the clipboard (with fallback toast).
   *
   * @param options.shareParams Arbitrary share parameters for your game.
   * @param options.metadata Optional OpenGraph metadata for rich previews.
   * @param options.slug Optional cosmetic slug appended to the share URL (e.g.
   *   "boss-level"). Sanitized server-side; non-unique; does not affect resolution.
   * @param options.target Optional content target: the piece this share is
   *   about, so its clicks credit that piece's author instead of your app.
   *   Omit for an ordinary app share.
   * @returns The generated share URL.
   */
  shareLinkAsync(options: {
    shareParams: Record<string, string>;
    metadata?: ShareMetadata;
    slug?: string;
    target?: ShareContentTarget;
  }): Promise<ShareLinkResult>;

  /**
   * Create a share document and return both the URL and a QR code image.
   *
   * Games can render or print the returned QR image or re-use the share URL.
   *
   * @param options.shareParams Arbitrary share parameters for your game.
   * @param options.metadata Optional OpenGraph metadata for the share.
   * @param options.qrOptions Customisation for the generated QR code.
   * @param options.slug Optional cosmetic slug appended to the share URL (e.g.
   *   "boss-level"). Sanitized server-side; non-unique; does not affect resolution.
   * @returns The generated share URL, QR code (as a data URL), and shareLinkId.
   */
  createQRCodeAsync(options: {
    shareParams: Record<string, string>;
    metadata?: ShareMetadata;
    qrOptions?: SocialQRCodeOptions;
    slug?: string;
    target?: ShareContentTarget;
  }): Promise<QRCodeResult>;

  /**
   * Register the pending share intent for this game session.
   *
   * While an intent is registered, the HOST app's own share buttons (game
   * toolbar, in-game info panel) share a tracked link carrying
   * `shareParams` instead of the plain game link. The recipient receives the
   * params through `app.resolveLaunchIntent()`.
   *
   * Cheap to call: the tracked link is pre-minted in the background so the
   * player's Share tap is instant — your call never blocks on that. Call it
   * whenever your shareable state changes (entering a level, joining a
   * room); each call replaces the previous intent. Consecutive calls with an
   * identical payload are deduplicated, so only real changes re-mint.
   *
   * The intent is scoped to the running session: it is discarded when the
   * game closes and never applies to other games or the app's game page
   * after exit. In the playground and mock environments there is no host
   * share button, so this is a validated no-op.
   *
   * Like every RPC, the promise rejects on transport-level failures —
   * including an app version too old to know this message. Wrap in
   * try/catch; a rejection simply means the app's share button keeps its
   * default link.
   *
   * @beta
   * @throws {RundotApiError} If `shareParams`/`metadata` exceed the share caps
   *   (20 keys, 100 KB values, 200 KB total).
   */
  setShareIntentAsync(options: ShareIntent): Promise<void>;

  /**
   * Clear the pending share intent. The host's share buttons revert to
   * sharing the plain game link. Call when the player leaves the shareable
   * state (e.g. back to the main menu).
   *
   * @beta
   */
  clearShareIntentAsync(): Promise<void>;

  /**
   * Write click metadata to a share as the current user (clicker).
   *
   * Metadata is a full replacement — subsequent calls overwrite the entire
   * metadata object. Games that need to preserve prior keys should send the
   * complete metadata each time.
   *
   * **Privacy note:** Click metadata is readable by any authenticated user
   * with the `shareLinkId`. Games must not write PII or sensitive user data.
   * Values are returned verbatim — games rendering metadata in HTML must
   * escape appropriately.
   *
   * @param options.shareLinkId The share to attach metadata to.
   * @param options.metadata Arbitrary string key-value pairs (max 20 keys,
   *   64-char keys, 1024-char values, 4 KB total).
   */
  addShareClickDataAsync(options: {
    shareLinkId: string;
    metadata: Record<string, string>;
  }): Promise<void>;

  /**
   * Query all click data for a share.
   *
   * Returns up to 200 click records ordered by `createdAt` ascending.
   * If results were capped, `truncated` is `true`.
   *
   * @param options.shareLinkId The share to query.
   */
  getShareClicksAsync(options: {
    shareLinkId: string;
  }): Promise<{ clicks: ShareClickData[]; truncated: boolean }>;

  /**
   * Get the current user's click data for a specific share.
   *
   * @param options.shareLinkId The share to query.
   * @returns The user's click data, or `null` if they haven't clicked.
   */
  getMyShareClickDataAsync(options: {
    shareLinkId: string;
  }): Promise<ShareClickData | null>;

  /**
   * Share a locally-created file (e.g. recorded gameplay MP4, screenshot) to
   * native share targets via the host's Web Share API.
   *
   * The file data crosses the bridge as base64. The host parent frame decodes
   * it and invokes `navigator.share({ files })` — no Permissions-Policy
   * delegation needed.
   *
   * @beta
   * @throws If the platform doesn't support file sharing, or the file fails
   *   MIME/size validation.
   */
  shareFileAsync(options: ShareFileOptions): Promise<ShareFileResult>;

  /**
   * Query whether the host supports file sharing. Games can use this to
   * feature-detect before recording/generating shareable content.
   *
   * @beta
   */
  canShareFileAsync(): Promise<CanShareFileResult>;

  /**
   * Compose a post to an external social platform (X, Reddit, TikTok,
   * Instagram) through host-owned UI.
   *
   * The game passes content only; the host owns the entire
   * (surface × destination) matrix: it renders a destination sheet, and the
   * user's tap inside that sheet opens the platform composer (X/Reddit) or the
   * OS share sheet with the caption pre-copied (TikTok/Instagram). If
   * `shareParams` is present the host mints a tracked share link and attaches
   * it to the post.
   *
   * This never rejects for "unsupported platform": when the `platform` hint
   * can't work on the current surface, the host falls back to its generic
   * destination chooser.
   *
   * @beta
   * @throws If text/title/subreddit/media fail validation.
   */
  composeSocialPostAsync(
    options: ComposeSocialPostOptions,
  ): Promise<ComposeSocialPostResult>;

  /**
   * Open the creator's X "follow me" link through host-owned UI. The game sends
   * only the platform enum — never a handle or URL. Resolves
   * `{ completed: false, reason: 'not_configured' }` (never throws) when the
   * creator hasn't set that handle.
   *
   * @beta
   */
  openXFollowMeLinkAsync(): Promise<FollowMeResult>;

  /**
   * Open the creator's Instagram "follow me" link. See
   * {@link openXFollowMeLinkAsync}.
   *
   * @beta
   */
  openInstagramFollowMeLinkAsync(): Promise<FollowMeResult>;

  /**
   * Open the creator's TikTok "follow me" link. See
   * {@link openXFollowMeLinkAsync}.
   *
   * @beta
   */
  openTikTokFollowMeLinkAsync(): Promise<FollowMeResult>;

  /**
   * Open the creator's Discord server invite. See
   * {@link openXFollowMeLinkAsync}.
   *
   * @beta
   */
  openDiscordFollowMeLinkAsync(): Promise<FollowMeResult>;

  /**
   * List which follow-me platforms the creator has configured. Lets games
   * feature-detect before showing a "follow us" button.
   *
   * @beta
   */
  getConfiguredFollowMePlatformsAsync(): Promise<FollowMePlatform[]>;
}
